import { create } from "zustand";
import { Scene, TourData } from "../Types";
import { AxiosProgressEvent } from "axios";

type dataRootStoreType = {
  root: string;
};

export const useDataRootStore = create<dataRootStoreType>()((set) => ({
  root: "https://editor.deepreality.cloud",
}));

export const useJWTStore = create<{ jwt?: string }>()(() => ({}));

type tourIDStore = {
  tourID?: number;
  setTourID: (number: number) => void;
};

export const useTourIDStore = create<tourIDStore>()((set) => ({
  tourID: undefined,
  setTourID: (number: number) => set({ tourID: number }),
}));

type currentSceneIDStoreType = {
  currentSceneID: number;
  currentScene?: Scene;
  setCurrentSceneID: (number: number) => void;
  setSceneIDOnLoad: (number?: number) => void;
  setCurrentScene: (sc: Scene) => void;
};
export const useCurrentSceneStore = create<currentSceneIDStoreType>()(
  (set, get) => ({
    currentSceneID: -1,
    currentScene: undefined,
    setCurrentSceneID: (number: number) => {
      console.debug("moving to scene " + number);
      set({
        currentSceneID: number,
      });
    },
    setCurrentScene: (sc: Scene) => set({ currentScene: sc }),
    setSceneIDOnLoad: (number?: number) => {
      if (number && get().currentSceneID === -1) {
        console.debug("moving to initial scene");
        set({ currentSceneID: number });
      } else return;
    },
  })
);

type parameterStore = {
  tourID: number;
  userID: number;
  isEditor: boolean;
  jwt: string;
  lang: "it" | "en";
  setTourID: (tourID: number) => void;
  setUserID: (userID: number) => void;
  setJwt: (jwt: string) => void;
  setIsEditor: (bool: boolean) => void;
  setLang: (lang: "it" | "en") => void;
};

export const useParametersStore = create<parameterStore>()((set) => ({
  tourID: 0,
  userID: 0,
  isEditor: false,
  jwt: "",
  lang: "it",
  setTourID: (tourID: number) => set({ tourID: tourID }),
  setUserID: (userID: number) => set({ userID: userID }),
  setJwt: (jwt: string) => set({ jwt: jwt }),
  setIsEditor: (bool: boolean) => set({ isEditor: bool }),
  setLang: (lang: "it" | "en") => set({ lang: lang }),
}));

type tourDataStore = {
  tourData: TourData | undefined;
  setTourData: (data: TourData) => void;
};

export const useTourDataStore = create<tourDataStore>()((set) => ({
  tourData: undefined,
  setTourData: (data: TourData) => set({ tourData: data }),
}));

type editorHotspotType = {
  hotspot: boolean;
  hotspotData?: string;
  openHotspot: (bool: boolean) => void;
  setHotspotData: (str?: string) => void;
};

export const editorHotspotStore = create<editorHotspotType>()((set) => ({
  hotspot: false,
  hotspotData: undefined,
  openHotspot: (bool: boolean) => set({ hotspot: bool }),
  setHotspotData: (str?: string) => set({ hotspotData: str }),
}));

type editorExperienceType = {
  // DRAG
  dragging: boolean;
  setDragging: (bool?: boolean) => void;
  stopDragging: () => void;

  //TRANSITION
  transition: boolean;
  setTransition: (bool: boolean) => void;
  stopTransition: () => void;
};

export const editorExperienceStore = create<editorExperienceType>()(
  (set, get) => ({
    dragging: false,
    setDragging: (bool?: boolean) => set({ dragging: bool || true }),
    stopDragging: () => set({ dragging: false }),

    transition: false,

    setTransition: (bool: boolean) => {
      if (bool === get().transition) return;
      else set({ transition: bool });
    },

    stopTransition: () => set({ transition: false }),
  })
);

type sceneStoreType = {
  scene?: Scene;
  setScene: (sc?: Scene) => void;
};

export const useSceneStore = create<sceneStoreType>()((set) => ({
  scene: undefined,
  setScene: (sc?: Scene) => set({ scene: sc }),
}));

type viewerStore = {
  viewer: boolean;
  setViewer: (bool: boolean) => void;
};

export const useViewerStore = create<viewerStore>()((set) => ({
  viewer: false,
  setViewer: (bool: boolean) => set({ viewer: bool }),
}));

type widePopupStoreType = {
  isOpen: boolean;
  isTakingScreenshot: boolean;
  target?: string;
  setIsOpen: (bool: boolean, target?: string) => void;
  setTakingScreen: (bool: boolean) => void;
  title?: string;
  setTitle: (str: string) => void;

  visionIsloading: boolean;
  setVisionIsLoading: (bool: boolean) => void;
};

export const useWidePopupStore = create<widePopupStoreType>()((set) => ({
  isOpen: false,
  isTakingScreenshot: false,

  setTakingScreen: (bool: boolean) =>
    set({ isOpen: !bool, isTakingScreenshot: bool }),
  setIsOpen: (bool: boolean, target?: string) =>
    set({ isOpen: bool, target: target }),
  title: undefined,
  setTitle: (str: string) => set({ title: str }),
  visionIsloading: false,
  setVisionIsLoading: (bool: boolean) => set({ visionIsloading: bool }),
}));

type screenshotRectangleStoreType = {
  position: {
    x: number;
    y: number;
    h: number;
    w: number;
    bottomSpace: boolean;
  };

  image?: string;
  setImage: (img: string) => void;
  resetImage: () => void;

  setPosition: (pos: {
    x: number;
    y: number;
    h: number;
    w: number;
    bottomSpace: boolean;
  }) => void;
};

export const useScreenshotRectangleStore =
  create<screenshotRectangleStoreType>()((set) => ({
    position: { x: 0, y: 0, h: 256, w: 256, bottomSpace: true },

    setImage: (img: string) => set({ image: img }),
    resetImage: () => set({ image: undefined }),
    setPosition: (pos: {
      x: number;
      y: number;
      h: number;
      w: number;
      bottomSpace: boolean;
    }) => set({ position: pos }),
  }));

type progressBarStoreType = {
  upProgress: number;
  dlProgress: number;
  setUpProgress: (prog: AxiosProgressEvent) => void;
  setUpComplete: () => void;
};

export const useProgressBarStore = create<progressBarStoreType>()((set) => ({
  upProgress: 0,
  dlProgress: 0,
  setUpProgress: (prog: AxiosProgressEvent) => {
    if (prog.total) {
      let percentage = prog.loaded / prog.total;
      set({ upProgress: percentage * 100 });
    }
  },
  setUpComplete: () => set({ upProgress: 0 }),
}));
interface languageFile {
  [key: string]: string;
}
type languageStore = {
  languageFile: languageFile | undefined;
  setLanguage: (t: languageFile) => void;
};

export const useLocalizationStore = create<languageStore>()((set) => ({
  languageFile: undefined,
  setLanguage: (t: languageFile) => set({ languageFile: t }),
}));

type editingElementStoreType = {
  type:
    | "scene"
    | "link"
    | "transition"
    | "popup"
    | "groups"
    | "quiz"
    | "audio"
    | "tour"
    | "escapeRoomPops";
  id: string | number;
  goBackTo?: string | number;
  setEditingElem: (
    type:
      | "scene"
      | "link"
      | "transition"
      | "popup"
      | "groups"
      | "quiz"
      | "audio"
      | "tour"
      | "escapeRoomPops",
    id: string | number,
    goBackTo?: string | number
  ) => void;
};

export const useEditingElementStore = create<editingElementStoreType>()(
  (set) => ({
    type: "tour",
    id: 0,
    setEditingElem: (type, id, goBackTo) =>
      set({ type: type, id: id, goBackTo: goBackTo || undefined }),
  })
);

type assetListStoreType = {
  isOpen: boolean;
  isLoading?: boolean;
  type?: 1 | 2 | 3;
  forMap?: boolean;
  mapID?: number;

  setOpen: (type: 1 | 2 | 3) => void;
  setClosed: () => void;
  setOpenForMap: (id: number) => void;
  setLoading: (bool: boolean) => void;
};

export const useAssetListStore = create<assetListStoreType>()((set) => ({
  isOpen: false,
  setOpen: (type: 1 | 2 | 3) => set({ isOpen: true, type: type }),
  setOpenForMap: (id: number) =>
    set({ isOpen: true, type: 2, forMap: true, mapID: id }),
  setClosed: () =>
    set({
      isOpen: false,
      isLoading: undefined,
      type: 1,
      forMap: undefined,
      mapID: undefined,
    }),
  setLoading: (bool: boolean) => set({ isLoading: bool }),
}));

type mapEditorStoreType = {
  isOpen: boolean;
  currentMap?: number;
  onPage?: 1 | 2;
  fromScene?: boolean;

  setOpen: (id?: number, bool?: boolean) => void;

  setClosed: () => void;
};

export const useMapEditorStore = create<mapEditorStoreType>()((set) => ({
  isOpen: false,
  setClosed: () =>
    set({
      isOpen: false,
      currentMap: 0,
      fromScene: false,
      onPage: 1,
    }),

  setOpen: (id?: number, bool?: boolean) => {
    set({ isOpen: true, currentMap: id });
    if (bool) set({ fromScene: bool, onPage: 2 });
  },
}));

// setMapList({
//   isOpen: true,
//   currentMap: currentScene?.map,
//   onPage: 2,
//   fromScene: true,
// })
